import React, { useState, useId } from 'react';
import { Upload, X } from 'lucide-react';

/**
 * Uploader d'image unique. Convertit le fichier en Base64 et retourne la string.
 * Props:
 *  - value: string (data URI ou URL existante)
 *  - onChange: (base64String) => void
 *  - label: string (affiché au-dessus du composant)
 *  - hint: string (ex: "1920x1080, max 3 MB")
 *  - maxSizeMB: number (défaut 3)
 *  - required: boolean
 */
const ImageUploader = ({ value, onChange, label, hint = '', maxSizeMB = 3, required = false }) => {
  const inputId = useId();
  const [error, setError] = useState('');

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');

    if (!file.type.startsWith('image/')) {
      setError('Le fichier doit être une image (JPG, PNG, WebP).');
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`L'image ne doit pas dépasser ${maxSizeMB} MB.`);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => onChange(reader.result);
    reader.onerror = () => setError('Erreur lors de la lecture du fichier.');
    reader.readAsDataURL(file);
    e.target.value = ''; // permet de re-uploader la même image
  };

  const clear = () => {
    onChange('');
    setError('');
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-semibold">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="aperçu" className="max-h-48 rounded-lg border-2 border-gray-200 object-contain bg-gray-50" />
          <button
            type="button"
            onClick={clear}
            className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow"
            aria-label="Supprimer l'image"
            data-testid="image-uploader-clear"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="mt-2">
            <input
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
              id={`uploader-${inputId}`}
            />
            <label
              htmlFor={`uploader-${inputId}`}
              className="inline-flex items-center cursor-pointer text-sm text-amber-700 hover:underline"
            >
              <Upload className="w-4 h-4 mr-1" /> Changer l'image
            </label>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-amber-400 transition-colors">
          <input
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
            id={`uploader-${inputId}`}
          />
          <label htmlFor={`uploader-${inputId}`} className="cursor-pointer block">
            <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
            <p className="text-gray-700 font-medium">Cliquez pour choisir une image depuis votre ordinateur</p>
            <p className="text-xs text-gray-500 mt-1">JPG, PNG, WebP — Max {maxSizeMB} MB</p>
            {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
          </label>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default ImageUploader;
